import { useState } from "react";
import { Link } from "@remix-run/react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Id } from "../../../../convex/_generated/dataModel";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
  BookOpenIcon,
  SparklesIcon,
  CheckIcon,
  XMarkIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowUpRightIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

export default function AdminKbDashboard() {
  const [expandedCategory, setExpandedCategory] = useState<string | null>("Getting Started");
  const [selectedArticleId, setSelectedArticleId] = useState<Id<"supportArticles"> | null>(null);
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string>("Getting Started");
  const [searchFilter, setSearchFilter] = useState("");

  // Edit modal / inline drawer state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editCategory, setEditCategory] = useState("Getting Started");
  const [editTags, setEditTags] = useState("");
  const [editIsFeatured, setEditIsFeatured] = useState(false);

  // Agent Topic Generator state
  const [newTopic, setNewTopic] = useState("");
  const [newTopicCategory, setNewTopicCategory] = useState("Getting Started");
  const [isGenerating, setIsGenerating] = useState(false);

  const DEFAULT_CATEGORIES = [
    "Getting Started",
    "Events & Attendance",
    "People & Groups",
    "Giving & Billing",
    "Settings",
  ];

  // Convex Queries & Mutations
  const publishedArticles = useQuery(api.supportArticles.getPublishedArticles, {}) || [];
  const drafts = useQuery(api.supportArticles.getDraftArticles, {}) || [];
  const fetchedCategories = useQuery(api.supportArticles.getCategories);
  const categories = Array.from(
    new Set([
      ...DEFAULT_CATEGORIES,
      ...(fetchedCategories && fetchedCategories.length > 0 ? fetchedCategories : []),
    ])
  );

  const approveDraft = useMutation(api.supportArticles.approveDraft);
  const rejectDraft = useMutation(api.supportArticles.rejectDraft);
  const createAgentDraft = useMutation(api.supportArticles.createAgentDraft);

  // Combine all articles (published + drafts)
  const allArticlesMap = new Map();
  publishedArticles.forEach((art) => allArticlesMap.set(art._id, art));
  drafts.forEach((art) => allArticlesMap.set(art._id, art));
  const allArticles = Array.from(allArticlesMap.values());

  const selectedArticle = selectedArticleId ? allArticlesMap.get(selectedArticleId) : null;

  // Filtered list for active category view
  const categoryArticles = allArticles.filter(
    (art) =>
      (!activeCategoryFilter || art.category === activeCategoryFilter) &&
      (!searchFilter ||
        art.title.toLowerCase().includes(searchFilter.toLowerCase()) ||
        art.content.toLowerCase().includes(searchFilter.toLowerCase()))
  );

  const handleSelectArticleToEdit = (art: typeof allArticles[0]) => {
    setSelectedArticleId(art._id);
    setEditTitle(art.title);
    setEditContent(art.draftContent || art.content);
    setEditCategory(art.category || "Getting Started");
    setEditTags(art.tags?.join(", ") || "");
    setEditIsFeatured(art.isFeatured || false);
    setIsEditing(true);
  };

  const handleApprove = async (artId: Id<"supportArticles">) => {
    const art = allArticlesMap.get(artId);
    if (!art) return;
    try {
      await approveDraft({
        id: artId,
        userId: art.createdBy || (artId as any),
        title: isEditing && selectedArticleId === artId ? editTitle : undefined,
        content: isEditing && selectedArticleId === artId ? editContent : undefined,
        category: isEditing && selectedArticleId === artId ? editCategory : undefined,
        tags: isEditing && selectedArticleId === artId && editTags ? editTags.split(",").map((t) => t.trim()) : undefined,
        isFeatured: isEditing && selectedArticleId === artId ? editIsFeatured : undefined,
      });
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to approve article:", err);
    }
  };

  const handleReject = async (artId: Id<"supportArticles">) => {
    const art = allArticlesMap.get(artId);
    if (!art) return;
    try {
      await rejectDraft({
        id: artId,
        userId: art.createdBy || (artId as any),
      });
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to reject article:", err);
    }
  };

  const handleGenerateAgentDraft = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTopic.trim() || isGenerating) return;

    setIsGenerating(true);
    try {
      const slug = newTopic
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

      const generatedContent = `# ${newTopic} Guide\n\n## Overview\nThis guide explains how to use ${newTopic} effectively in MinHub.\n\n## Step-by-Step Instructions\n1. Log in to your MinHub portal.\n2. Access the ${newTopicCategory} section.\n3. Follow the onscreen workflow and click Save.\n\n## Best Practices\n- Verify all member records before saving.\n- Check permissions for ministry leaders.\n`;

      const newId = await createAgentDraft({
        title: newTopic.trim(),
        slug,
        draftContent: generatedContent,
        excerpt: `Complete step-by-step guide for ${newTopic}.`,
        category: newTopicCategory,
        tags: [newTopicCategory.toLowerCase().replace(/\s+/g, "-"), "guide"],
        outline: ["Overview", "Step-by-Step Instructions", "Best Practices"],
        userId: (publishedArticles[0]?.createdBy as any) || (drafts[0]?._id as any),
      });

      setNewTopic("");
      setActiveCategoryFilter(newTopicCategory);
    } catch (err) {
      console.error("Failed to generate agent draft:", err);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="w-full h-full min-h-screen overflow-y-auto bg-[#FBF9F7] font-sans text-darkGrey flex flex-col">
      {/* Top Header Bar matching Kit.com style */}
      <header className="border-b border-slate-200 bg-[#FAF8F5] px-6 py-4 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-primary text-white flex items-center justify-center font-extrabold text-xs shadow">
            MH
          </div>
          <span className="font-extrabold text-base tracking-tight text-darkGrey">MinHub Admin Documentation</span>
        </div>

        {/* Global Search input */}
        <div className="relative w-72">
          <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-2.5 pointer-events-none" />
          <input
            type="text"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Search all articles..."
            className="w-full pl-9 pr-4 py-1.5 bg-white text-xs text-darkGrey rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </header>

      {/* Main Kit Layout Container: Left Category Tree Sidebar | Right Content Panel */}
      <div className="flex-1 flex flex-col md:flex-row">
        {/* Left Sidebar: Collection & Category Tree */}
        <aside className="w-full md:w-72 bg-[#FAF8F5] border-r border-slate-200 p-4 space-y-2 shrink-0">
          <div className="px-2 py-1.5 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
            Collections & Categories
          </div>

          <div className="space-y-1">
            {categories.map((cat) => {
              const isExpanded = expandedCategory === cat;
              const isSelected = activeCategoryFilter === cat;
              const catArticles = allArticles.filter((a) => a.category === cat);

              return (
                <div key={cat} className="space-y-1">
                  <button
                    onClick={() => {
                      setActiveCategoryFilter(cat);
                      setExpandedCategory(isExpanded ? null : cat);
                    }}
                    className={`w-full flex items-center justify-between p-2.5 rounded-xl text-xs font-bold transition-all ${
                      isSelected
                        ? "bg-white text-primary border border-slate-200 shadow-sm"
                        : "text-darkGrey hover:bg-slate-200/50"
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <FolderIcon className={`w-4 h-4 ${isSelected ? "text-primary" : "text-slate-400"}`} />
                      <span className="truncate">{cat}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] bg-slate-200/60 text-slate-600 px-1.5 py-0.5 rounded-full font-mono">
                        {catArticles.length}
                      </span>
                      {isExpanded ? (
                        <ChevronDownIcon className="w-3.5 h-3.5 text-slate-400" />
                      ) : (
                        <ChevronRightIcon className="w-3.5 h-3.5 text-slate-400" />
                      )}
                    </div>
                  </button>

                  {/* Sub-tree articles */}
                  {isExpanded && (
                    <div className="pl-6 space-y-1 border-l-2 border-slate-200 ml-3 py-1">
                      {catArticles.map((art) => (
                        <button
                          key={art._id}
                          onClick={() => handleSelectArticleToEdit(art)}
                          className={`w-full text-left py-1.5 px-2 rounded-lg text-[11px] flex items-center justify-between transition-colors ${
                            selectedArticleId === art._id
                              ? "bg-primary/10 text-primary font-bold"
                              : "text-slate-600 hover:text-darkGrey hover:bg-slate-200/40"
                          }`}
                        >
                          <span className="truncate mr-1">{art.title}</span>
                          {!art.isPublished && (
                            <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-1 rounded border border-amber-200 shrink-0">
                              Draft
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* Right Main Content Panel */}
        <main className="flex-1 p-6 md:p-10 max-w-5xl">
          {/* Breadcrumb Navigation matching Kit */}
          <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
            <button onClick={() => setActiveCategoryFilter("")} className="hover:text-darkGrey transition-colors">
              All Collections
            </button>
            <ChevronRightIcon className="w-3 h-3 text-slate-300" />
            <span className="font-semibold text-darkGrey">{activeCategoryFilter || "All Articles"}</span>
          </div>

          {/* Collection Title & Subtitle */}
          <div className="mb-8">
            <h1 className="text-3xl font-extrabold text-darkGrey tracking-tight mb-2">
              {activeCategoryFilter || "All Knowledge Articles"}
            </h1>
            <p className="text-sm text-slate-500 max-w-2xl">
              Everything your ministry leaders and staff need to configure and manage {activeCategoryFilter || "MinHub"}.
            </p>
            <div className="text-xs font-medium text-slate-400 mt-3">
              {categoryArticles.length} article{categoryArticles.length !== 1 ? "s" : ""}
            </div>
          </div>

          {/* Quick AI Agent Article Generator Bar */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm mb-8">
            <div className="flex items-center gap-2 mb-2">
              <SparklesIcon className="w-4 h-4 text-secondary" />
              <h3 className="text-xs font-bold text-darkGrey">Prompt AI Agent to Write Documentation</h3>
            </div>
            <form onSubmit={handleGenerateAgentDraft} className="flex gap-2">
              <input
                type="text"
                value={newTopic}
                onChange={(e) => setNewTopic(e.target.value)}
                placeholder="Feature or topic name e.g. Smart Group Filter Predicates..."
                className="flex-1 px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <select
                value={newTopicCategory}
                onChange={(e) => setNewTopicCategory(e.target.value)}
                className="px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                disabled={!newTopic.trim() || isGenerating}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-white text-xs font-bold rounded-xl disabled:opacity-50 transition-colors shrink-0 flex items-center gap-1.5"
              >
                <PlusIcon className="w-4 h-4" />
                {isGenerating ? "Drafting..." : "Create Draft"}
              </button>
            </form>
          </div>

          {/* Inline Editor Modal/Drawer when editing an article */}
          {isEditing && selectedArticle && (
            <div className="bg-white p-6 rounded-2xl border-2 border-primary shadow-xl mb-8 space-y-4 animate-slide-top">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  <PencilSquareIcon className="w-5 h-5 text-primary" />
                  <h3 className="text-base font-bold text-darkGrey">Editing Article Draft</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleReject(selectedArticle._id)}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-red-50 text-slate-700 hover:text-red-600 text-xs font-semibold rounded-xl border border-slate-200 transition-colors flex items-center gap-1"
                  >
                    <XMarkIcon className="w-4 h-4" />
                    Archive
                  </button>
                  <button
                    onClick={() => handleApprove(selectedArticle._id)}
                    className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors shadow-sm flex items-center gap-1"
                  >
                    <CheckIcon className="w-4 h-4" />
                    Approve & Publish
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="p-1.5 text-slate-400 hover:text-darkGrey"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-3 text-xs">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Category</label>
                    <select
                      value={editCategory}
                      onChange={(e) => setEditCategory(e.target.value)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl"
                    >
                      {categories.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Tags</label>
                    <input
                      type="text"
                      value={editTags}
                      onChange={(e) => setEditTags(e.target.value)}
                      className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Markdown Body</label>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={12}
                    className="w-full p-3 font-mono text-xs bg-slate-900 text-slate-100 rounded-xl focus:outline-none leading-relaxed"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Collections & Articles Cards List */}
          <div className="space-y-3">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Articles in Collection</h2>

            {categoryArticles.length > 0 ? (
              categoryArticles.map((art) => (
                <div
                  key={art._id}
                  className="bg-white p-5 rounded-2xl border border-slate-200 hover:border-slate-300 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-darkGrey text-base hover:text-primary transition-colors">
                        {art.title}
                      </span>
                      {art.isPublished ? (
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                          Published
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                          Draft Awaiting Review
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2">
                      {art.excerpt || art.draftContent || art.content.slice(0, 140)}...
                    </p>
                    <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-2">
                      <span className="flex items-center gap-1">
                        <EyeIcon className="w-3.5 h-3.5" />
                        {art.viewCount || 0} views
                      </span>
                      <span>&bull;</span>
                      <span>Category: {art.category || "General"}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleSelectArticleToEdit(art)}
                      className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-darkGrey text-xs font-semibold rounded-xl border border-slate-200 transition-colors flex items-center gap-1"
                    >
                      <PencilSquareIcon className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    {!art.isPublished && (
                      <button
                        onClick={() => handleApprove(art._id)}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-colors shadow-sm flex items-center gap-1"
                      >
                        <CheckIcon className="w-3.5 h-3.5" />
                        Publish
                      </button>
                    )}
                    <Link
                      to={`/help/article/${art.slug}`}
                      className="p-2 text-slate-400 hover:text-primary transition-colors"
                      title="View Article Page"
                    >
                      <ArrowUpRightIcon className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-12 text-center bg-white rounded-2xl border border-dashed border-slate-200 text-slate-400 text-xs">
                No articles found in this collection.
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
